/* mmtjs v0.1.0 (ESM)
 * A focused Moment.js compatibility layer over Day.js
 * Goal: allow replacing `moment` → `mmtjs` for common use-cases.
 *
 * IMPORTANT:
 * - This file imports Day.js and several plugins directly and extends them.
 * - If you don't need some features, you can remove the corresponding imports + extends.
 * - Immutable semantics: Day.js is immutable; this shim mutates `this.$d` to preserve moment-like chainability.
 */

import dayjs from "dayjs";

// ---- plugins (trim this list if you don't need some features) ----
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import duration from "dayjs/plugin/duration.js";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import isoWeek from "dayjs/plugin/isoWeek.js";
import weekOfYear from "dayjs/plugin/weekOfYear.js";
import isBetween from "dayjs/plugin/isBetween.js";
import calendar from "dayjs/plugin/calendar.js";

// Extend the imported dayjs instance (environment-agnostic: no globals)
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(calendar);

// --------------------------
// helpers
// --------------------------
const UNITS_ALIAS = {
	ms: "millisecond",
	millisecond: "millisecond",
	milliseconds: "millisecond",
	s: "second",
	second: "second",
	seconds: "second",
	m: "minute",
	minute: "minute",
	minutes: "minute",
	h: "hour",
	hour: "hour",
	hours: "hour",
	d: "day",
	day: "day",
	days: "day",
	w: "week",
	week: "week",
	weeks: "week",
	M: "month",
	month: "month",
	months: "month",
	Q: "quarter",
	quarter: "quarter",
	quarters: "quarter",
	y: "year",
	year: "year",
	years: "year",
};
const normalizeUnit = (u) => UNITS_ALIAS[u] || u;

const toDayjs = (val) => (val instanceof MMT ? val.$d : dayjs(val));

// --------------------------
// Core wrapper type
// --------------------------
class MMT {
	constructor(input, format, strict) {
		// moment(input?, format?, strict?)
		if (format !== undefined) {
			this.$d = dayjs(input, format, strict);
		} else {
			this.$d = dayjs(input);
		}
	}

	// --- core ---
	clone() {
		return new MMT(this.$d);
	}
	valueOf() {
		return this.$d.valueOf();
	}
	toDate() {
		return this.$d.toDate();
	}
	toISOString() {
		return this.$d.toISOString();
	}
	toJSON() {
		return this.$d.toJSON();
	}
	unix() {
		return Math.floor(this.$d.valueOf() / 1000);
	}
	isValid() {
		return this.$d.isValid();
	}
	toString() {
		return this.$d.toString();
	}

	// --- formatting / locale ---
	format(fmt) {
		return fmt ? this.$d.format(fmt) : this.$d.format();
	}
	locale(loc) {
		if (loc) {
			this.$d = this.$d.locale(loc);
			return this;
		}
		return this.$d.locale();
	}

	// --- getters/setters (moment-style chainability) ---
	year(v) {
		return v === undefined ? this.$d.year() : ((this.$d = this.$d.year(v)), this);
	}
	month(v) {
		return v === undefined ? this.$d.month() : ((this.$d = this.$d.month(v)), this);
	}
	date(v) {
		return v === undefined ? this.$d.date() : ((this.$d = this.$d.date(v)), this);
	}
	hour(v) {
		return v === undefined ? this.$d.hour() : ((this.$d = this.$d.hour(v)), this);
	}
	minute(v) {
		return v === undefined ? this.$d.minute() : ((this.$d = this.$d.minute(v)), this);
	}
	second(v) {
		return v === undefined ? this.$d.second() : ((this.$d = this.$d.second(v)), this);
	}
	millisecond(v) {
		return v === undefined ? this.$d.millisecond() : ((this.$d = this.$d.millisecond(v)), this);
	}
	// aliases
	years(v) {
		return this.year(v);
	}
	months(v) {
		return this.month(v);
	}
	dates(v) {
		return this.date(v);
	}
	hours(v) {
		return this.hour(v);
	}
	minutes(v) {
		return this.minute(v);
	}
	seconds(v) {
		return this.second(v);
	}
	milliseconds(v) {
		return this.millisecond(v);
	}

	// --- math ---
	add(amount, unit) {
		this.$d = this.$d.add(amount, normalizeUnit(unit));
		return this;
	}
	subtract(amount, unit) {
		this.$d = this.$d.subtract(amount, normalizeUnit(unit));
		return this;
	}
	startOf(unit) {
		this.$d = this.$d.startOf(normalizeUnit(unit));
		return this;
	}
	endOf(unit) {
		this.$d = this.$d.endOf(normalizeUnit(unit));
		return this;
	}
	diff(input, unit, float) {
		const d = toDayjs(input);
		return this.$d.diff(d, normalizeUnit(unit), float);
	}

	// --- comparisons ---
	isBefore(input, unit) {
		const d = toDayjs(input);
		return unit ? this.$d.isBefore(d, normalizeUnit(unit)) : this.$d.isBefore(d);
	}
	isAfter(input, unit) {
		const d = toDayjs(input);
		return unit ? this.$d.isAfter(d, normalizeUnit(unit)) : this.$d.isAfter(d);
	}
	isSame(input, unit) {
		const d = toDayjs(input);
		return unit ? this.$d.isSame(d, normalizeUnit(unit)) : this.$d.isSame(d);
	}
	isSameOrBefore(input, unit) {
		const d = toDayjs(input);
		return this.$d.isSameOrBefore(d, unit ? normalizeUnit(unit) : undefined);
	}
	isSameOrAfter(input, unit) {
		const d = toDayjs(input);
		return this.$d.isSameOrAfter(d, unit ? normalizeUnit(unit) : undefined);
	}
	isBetween(a, b, unit, inclusivity) {
		const da = toDayjs(a);
		const db = toDayjs(b);
		return this.$d.isBetween(da, db, unit ? normalizeUnit(unit) : undefined, inclusivity);
	}

	// --- relative time ---
	fromNow(withoutSuffix) {
		return this.$d.fromNow(withoutSuffix);
	}
	from(input, withoutSuffix) {
		return this.$d.from(toDayjs(input), withoutSuffix);
	}
	to(input, withoutSuffix) {
		return this.$d.to(toDayjs(input), withoutSuffix);
	}
	toNow(withoutSuffix) {
		return this.$d.toNow(withoutSuffix);
	}

	// --- tz/utc ---
	utc() {
		this.$d = this.$d.utc();
		return this;
	}
	local() {
		this.$d = this.$d.local();
		return this;
	}
	tz(zone) {
		if (zone) {
			this.$d = this.$d.tz(zone);
			return this;
		}
		// Return current zone string if available (moment.tz() returns moment object; we mirror dayjs behavior for no-arg)
		return this.$d.tz();
	}
	utcOffset(offsetMinutes) {
		if (offsetMinutes === undefined) return this.$d.utcOffset();
		this.$d = this.$d.utcOffset(offsetMinutes);
		return this;
	}

	// --- week helpers ---
	week(v) {
		return v === undefined ? this.$d.week() : ((this.$d = this.$d.week(v)), this);
	}
	isoWeek() {
		return this.$d.isoWeek();
	}

	// --- serialization helpers ---
	toArray() {
		return [this.year(), this.month(), this.date(), this.hour(), this.minute(), this.second(), this.millisecond()];
	}
	toObject() {
		return {
			years: this.year(),
			months: this.month(),
			date: this.date(),
			hours: this.hour(),
			minutes: this.minute(),
			seconds: this.second(),
			milliseconds: this.millisecond(),
		};
	}
}

// --------------------------
// main callable: mmtjs()
// --------------------------
function mmtjs(input, format, strict) {
	return new MMT(input, format, strict);
}

// --------------------------
// static helpers (subset of moment statics)
// --------------------------
mmtjs.version = "0.1.0";
mmtjs.fn = MMT.prototype;

mmtjs.isMoment = (obj) => obj instanceof MMT;

mmtjs.now = () => Date.now();

mmtjs.unix = (secs) => new MMT(dayjs.unix(secs));

mmtjs.utc = (input, format, strict) => {
	// Support same signatures as moment.utc(...)
	const d = format !== undefined ? dayjs.utc(input, format, strict) : dayjs.utc(input);
	const m = new MMT(d);
	m.$d = d; // ensure the wrapped instance is the UTC one
	return m;
};

mmtjs.tz = (input, zone) => {
	const d = zone ? dayjs.tz(input, zone) : dayjs.tz(input);
	const m = new MMT(d);
	m.$d = d;
	return m;
};
mmtjs.tzGuess = () => (dayjs.tz && typeof dayjs.tz.guess === "function" ? dayjs.tz.guess() : undefined);

mmtjs.max = (...args) => {
	const ds = args.map((a) => toDayjs(a));
	return new MMT(dayjs.max(ds));
};
mmtjs.min = (...args) => {
	const ds = args.map((a) => toDayjs(a));
	return new MMT(dayjs.min(ds));
};

// duration passthrough (returns Day.js duration object)
mmtjs.duration = (...args) => dayjs.duration(...args);

// locale passthrough
mmtjs.locale = (...args) => dayjs.locale(...args);

// Named + default export
export {mmtjs as default, mmtjs, MMT};
